import { createTheme, Primitives } from "baseui";

const primitives: Partial<Primitives> = {
  // Gray
  mono100: "#F8F8F8",
  mono200: "#EFEFEF",
  mono300: "#E0E0E0",
  mono400: "#D6D6D6",
  mono500: "#C2C2C2",
  mono600: "#999999",
  mono700: "#666666",
  mono800: "#3D3D3D",
  mono900: "#292929",
  mono1000: "#1F1F1F",

  // Blue
  primaryA: "#2BB9F5",
  primary50: "#092531",
  primary100: "#1AA0E2",
  primary200: "#B5E6FA",
  primary300: "#E2F5FE",

  // Only used for sidebars etc
  primary400: "#1B556B",
  primary500: "#164456",
  primary600: "#103340",

  // Orange
  accent: "#F7933B",
  accent50: "#984A04",
  accent100: "#D46706",
  accent200: "#FAAb65",
  accent300: "#FFEEDE",

  // Red
  negative: "#F24851",
  negative50: "#BA071F",
  negative100: "#E82637",
  negative200: "#F0979A",
  negative300: "#FFEAEE",

  // Yellow
  warning: "#FEDC56",
  warning50: "#B18E00",
  warning100: "#EDBE04",
  warning200: "#FEF4AB",
  warning300: "#FFFDEB",

  // Green
  positive: "#86C840",
  positive50: "#216F0E",
  positive100: "#61A52D",
  positive200: "#C4E4A1",
  positive300: "#F1F9E8",
};

const overrides = {
  colors: {
    // Button primary
    buttonPrimaryFill: "#F7933B",
    buttonPrimaryText: "#FFFFFF",
    buttonPrimaryHover: "#FAAb65",
    buttonPrimaryActive: "#FAAb65",
    buttonPrimaryGhostHover: "#FFEEDE",

    buttonPrimarySpinnerForeground: "#F8F8F8",
    buttonPrimarySpinnerBackground: "#FAAb65",

    // Button secondary
    buttonSecondaryFill: "transparent",
    buttonSecondaryText: "#F7933B",
    buttonSecondaryHover: "#FAAb65",
    buttonSecondaryActive: "#D46706",

    buttonSecondarySpinnerForeground: "#F7933B",
    buttonSecondarySpinnerBackground: "#B5E6FA",

    // Button tertiary
    buttonTertiaryFill: "#2BB9F5",
    buttonTertiaryText: "#FFFFFF",
    buttonTertiaryHover: "#B5E6FA",
    buttonTertiaryActive: "#B5E6FA",

    buttonTertiarySpinnerForeground: "#2BB9F5",
    buttonTertiarySpinnerBackground: "#1B556B",

    // Tables
    tableStripedBackground: "#EFEFEF",

    // Links
    linkText: "#2BB9F5",
    linkVisited: "#1AA0E2",
    linkHover: "#1AA0E2",
    linkActive: "#1AA0E2",

    // Content
    contentPrimary: "#1F1F1F",
    contentSecondary: "#666666",
    contentTertiary: "#999999",

    // Background
    backgroundPrimary: "#F8F8F8",
    backgroundSecondary: "#EFEFEF",
    backgroundTertiary: "#D6D6D6",

    // Notification
    notificationPrimaryBackground: "#F8F8F8",
    notificationPrimaryText: "#2BB9F5",

    notificationInfoBackground: "#2BB9F5",
    notificationInfoText: "#E2F5FE",

    notificationPositiveBackground: "#86C840",
    notificationPositiveText: "#F1F9E8",

    notificationWarningBackground: "#FEDC56",
    notificationWarningText: "#FFFDEB",

    notificationNegativeBackground: "#F24851",
    notificationNegativeText: "#FFEAEE",

    // Info
    info: "#F7933B",

    // Borders
    borderOpaque: "#D6D6D6",
    borderSelected: "#999999",
    borderTransparent: "transparent",

    // Accent states
    accentHover: "#FAAb65",
    accentActive: "#D46706",
    accentDisabled: "#FFEEDE",

    // Custom primitive colors
    primaryA: "#2BB9F5",
    primary50: "#092531",
    primary100: "#1AA0E2",
    primary200: "#B5E6FA",
    primary300: "#E2F5FE",
    primary400: "#1B556B",
    primary500: "#164456",
    primary600: "#103340",
    accent: "#F7933B",
    accent50: "#984A04",
    accent100: "#D46706",
    accent200: "#FAAb65",
    accent300: "#FFEEDE",

    actionRedDark: "#DE0A12",
    alertBlue: "#e6f8fe",
    alertRed: "#F8DADC",
    blue: "#00adf2",
    green: "#6cc123",
    red: "#ff1c25",
    orange: "#ff8116",
    orangeDark: "#FF8100",
    orangeLight: "#FFE6CC",
    dark: "#010F14",
    gray: "#e6e6e6",
    lightGray: "#ACACAC",
    yellow: "#fefad6",
    yellowDark: "#ffe5ac",
    primary: "#d8b379",
    lightRed: "#fbe2e3",
    lightGreen: "#f0f9e3",
    brandBlue: "#154D60",
    turquoise: "#DAE8ED",
    purple: "#5D4EA2",
    brandGoldDark: "#BA7C1C",
    black: "#000",
    defaultRoomState: "#E2E2E2",

    ...primitives
  },
  sizing: {
    scale100: "2px",
    scale200: "4px",
    scale400: "8px",
    scale500: "10px",
    scale600: "12px",
    scale800: "16px",
    scale1000: "20px",
    scale1200: "24px",
    scale1600: "32px",
    scale1800: "48px",
  },
  borders: {
    radius100: "2px",
    radius200: "4px",
    radius300: "8px",
    radius400: "12px",
    radius500: "16px",
    radius600: "20px",
  },
};

const baseTheme = createTheme(primitives);

const typography = {
  ...baseTheme.typography,
  // Override fontFamily for all typography variants
  ...Object.fromEntries(
    Object.entries(baseTheme.typography).map(([key, value]) => [
      key,
      { ...value, fontFamily: "SourceSansPro-Regular" },
    ])
  ),
} as typeof baseTheme.typography;

// Manually add custom colors that BaseUI doesn't support
export default {
  ...baseTheme,
  sizing: {
    ...baseTheme.sizing,
    ...overrides.sizing,
  },
  colors: {
    ...baseTheme.colors,
    ...overrides.colors,
  },
  typography: typography,
  borders: {
    ...baseTheme.borders,
    ...overrides.borders,
  },
};
